import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import FocusLock from 'react-focus-lock';
import cn from 'classnames';
import { v4 as uuidv4 } from 'uuid';
import ButtonWithIcon from 'chameleon/ui-stack/ui/buttons/button-with-icon';
import ModalTitle from './ModalTitle';
import styles from './styles.css';
import throttle from 'lodash/throttle';

/**
 * @todo
 * Check diffs for accessibility missed props
 * */

const SIZES = {
  tiny: {
    wrapper: 'desktop:w-3/12 desktop:min-w-160',
  },
  small: {
    wrapper: 'desktop:w-4/12 desktop:min-w-200',
  },
  medium: {
    wrapper: 'desktop:w-5/12 desktop:min-w-260',
  },
  big: {
    wrapper: 'desktop:w-7/12 desktop:min-w-320',
  },
  huge: {
    wrapper: 'desktop:w-90-screen',
  },
  full: {
    wrapper: 'desktop:w-full',
  },
};

const Modal = (props) => {
  const {
    onClose,
    mainContainerId,
    title,
    subTitle,
    topBar,
    dialogClassName,
    contentClassName,
    wrapperClassName,
    headingClassName,
    hideBehindScroll,
    suppressEscapeKeyBehavior,
    showCloseButton,
    children,
    returnFocus,
    focusLockGroup,
    headerWithBorder,
    topBarClassName,
    bottomContent,
    contentLabel,
    aria,
    contentWithSpacing,
    size,
    isContainingBlock,
    isScrollableContent,
    hasTitleWithTooltip,
  } = props;

  const modalUUID = uuidv4();
  const closeButtonRef = useRef();
  const modalWindowRef = useRef();
  const modalContainerRef = useRef();
  const bodyElementRef = useRef(document.querySelector('body'));

  useEffect(() => {
    const bodyClassName = `overflow-hidden--${modalUUID}`;
    if (bodyElementRef && bodyElementRef.current && hideBehindScroll) {
      bodyElementRef.current.classList.add(bodyClassName);
    }
    if (modalContainerRef && modalContainerRef.current) {
      modalContainerRef.current.focus();
    }

    return () => {
      if (bodyElementRef && bodyElementRef.current) {
        bodyElementRef.current.classList.remove(bodyClassName);
      }
    };
  }, []);

  useEffect(() => {
    const { activeElement } = document;
    return () => {
      if (activeElement) {
        setTimeout(() => {
          activeElement.focus();
        }, 0);
      }
    };
  }, []);

  useEffect(() => {
    const documentHeight = throttle(() => {
      const doc = document.documentElement;
      doc.style.setProperty('--doc-height', `${window.innerHeight}px`);
    }, 200);

    window.addEventListener('resize', documentHeight);
    documentHeight();
    return () => window.removeEventListener('resize', documentHeight);
  }, []);

  const onKeyDown = (event) => {
    const { keyCode } = event;
    // handle ESC key press
    if (keyCode === 27 && !suppressEscapeKeyBehavior) {
      onClose(event);
    }
  };

  const isFullPage = size === 'full';
  const containingBlock = {
    contain: 'paint',
  };

  return (
    <FocusLock group={focusLockGroup} returnFocus={returnFocus}>
      <div
        id={mainContainerId}
        ref={modalContainerRef}
        className={cn(
          'base-modal',
          dialogClassName,
          'w-full fixed top-0 left-0 z-max cursor-default',
          'flex justify-center items-center',
          'bg-gray-900 bg-opacity-10',
          {
            'h-full': !isFullPage,
            'h-screen': isFullPage,
          },
        )}
        onKeyDown={onKeyDown}
      >
        <div
          {...aria}
          aria-modal="true"
          /* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */
          tabIndex={0}
          role={isFullPage ? null : 'dialog'}
          aria-label={contentLabel || null}
          aria-labelledby={`modal-title--${modalUUID}`}
          style={isContainingBlock ? containingBlock : {}}
          className={cn(
            styles['modal-wrapper'],
            wrapperClassName,
            SIZES[size].wrapper,
            'bg-white flex flex-col border border-solid border-gray-400 shadow-xl',
            'mobile:absolute mobile:inset-0 mobile:w-screen mobile:h-screen-mobile-fix',
            {
              'ie-height': !!document.documentMode, //verify if IE still needs support
              'bg-white outline-none h-full min-w-0': isFullPage,
              'desktop:max-h-95-screen desktop:rounded-0.8': !isFullPage,
            },
          )}
        >
          <div
            className={cn(topBarClassName, 'flex items-center', {
              'border-b border-gray-300': headerWithBorder,
            })}
          >
            {title && (
              <ModalTitle
                title={title}
                modalUUID={modalUUID}
                className={headingClassName}
                hasTitleWithTooltip={hasTitleWithTooltip}
              />
            )}
            {subTitle && (
              <div className="flex items-center" data-test-id="modal-subtitle">
                {subTitle}
              </div>
            )}
            {topBar && (
              <div
                data-test-id="modal-topbar"
                className="flex items-center flex-grow justify-end mr-5"
              >
                {topBar}
              </div>
            )}
            {showCloseButton && (
              <div
                className={cn(
                  'flex items-center justify-center h-16.8 px-4 ml-auto',
                  {
                    'border-l border-gray-300': headerWithBorder,
                  },
                )}
              >
                <ButtonWithIcon
                  data-test-id="modal-close-button"
                  isIcon
                  isNaked
                  size="tiny"
                  tabIndex={0}
                  icon="close"
                  onClick={onClose}
                  aria-label="Close"
                  ref={closeButtonRef}
                />
              </div>
            )}
          </div>
          <div
            className={cn(
              {
                'p-6': contentWithSpacing,
                'overflow-auto': isScrollableContent,
                'mobile:h-full': !isFullPage,
                'h-full': isFullPage,
              },
              contentClassName,
            )}
            ref={modalWindowRef}
          >
            {children}
          </div>
          {bottomContent}
        </div>
      </div>
    </FocusLock>
  );
};

Modal.propTypes = {
  // Callback for closing the modal and X button
  onClose: PropTypes.func,
  mainContainerId: PropTypes.string,
  // The title of the modal
  title: PropTypes.any,
  // The title of the modal
  subTitle: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  // Places content in the top bar between title and X
  topBar: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  // The class to style the dialog window.
  dialogClassName: PropTypes.string,
  // The class to style the modal content.
  contentClassName: PropTypes.string,
  // The class to style the modal wrapper.
  wrapperClassName: PropTypes.string,
  // The class to style the modal heading title.
  headingClassName: PropTypes.string,
  //className: PropTypes.string, - check usages
  // Apply class to hide the scrollbar from the document.
  hideBehindScroll: PropTypes.bool,
  // If true, ESC won't close the modal
  suppressEscapeKeyBehavior: PropTypes.bool,
  // Handler for displaying X button.
  showCloseButton: PropTypes.bool,
  //Returns focus by FocusLock
  returnFocus: PropTypes.bool,
  //Groups the focus
  focusLockGroup: PropTypes.string,
  // If true the top bar will have borders
  headerWithBorder: PropTypes.bool,
  // If true the content receives spacing tailwind classes (margins, paddings)
  contentWithSpacing: PropTypes.bool,
  // The class to style the top bar
  topBarClassName: PropTypes.string,
  // Used tp draw bottom content, in general buttons
  bottomContent: PropTypes.any,
  // The accessibility content label
  contentLabel: PropTypes.string,
  // Object for setting accessibility aria options
  aria: PropTypes.object,
  // Sets the size of the modal
  size: PropTypes.oneOf(Object.keys(SIZES)),
  // Transforms the wrapper container into a Containing Block
  isContainingBlock: PropTypes.bool,
  // If true it removes the scrolls from modal body
  isScrollableContent: PropTypes.bool,
  // If true the title tooltip algorithm is used to determine
  // if title needs tooltip
  hasTitleWithTooltip: PropTypes.bool,
};

Modal.defaultProps = {
  onClose: () => {},
  mainContainerId: null,
  title: <div />,
  subTitle: null,
  topBar: null,
  dialogClassName: null,
  contentClassName: null,
  wrapperClassName: 'min-w-50-screen',
  headingClassName: null,
  hideBehindScroll: true,
  suppressEscapeKeyBehavior: false,
  showCloseButton: true,
  returnFocus: false,
  focusLockGroup: null,
  headerWithBorder: true,
  contentWithSpacing: true,
  topBarClassName: null,
  bottomContent: null,
  contentLabel: null,
  aria: {},
  size: 'small',
  isContainingBlock: false,
  isScrollableContent: true,
  hasTitleWithTooltip: true,
};

export default Modal;
